import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Send, Sparkles } from "lucide-react";
import { PRODUCT_SUGGESTIONS, QUICK_OPTIONS, analyzeInput } from "./productFinderData";
import { Button } from "./ui/button";
import { useNavigate } from "react-router-dom";

export default function ProductFinderChat({ isOpen, onClose }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showQuickOptions, setShowQuickOptions] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setMessages([{
        type: "bot",
        content: "Hi! 👋 I'm Happy, your AI assistant here to help you find the perfect product for your celebration. What are you looking for today?",
      }]);
      setShowQuickOptions(true);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleQuickOption = (key) => {
    const option = QUICK_OPTIONS.find(o => o.key === key);
    setMessages(prev => [...prev, { type: "user", content: option.label }]);
    setShowQuickOptions(false);
    setIsTyping(true);

    setTimeout(() => {
      setIsTyping(false);
      const suggestion = PRODUCT_SUGGESTIONS[key];
      setMessages(prev => [...prev, {
        type: "bot",
        content: `Great choice! Here are our best products for ${suggestion.title}:`,
        products: suggestion.products
      }]);
    }, 800);
  };

  const handleSend = () => {
    if (!inputValue.trim()) return;

    const userMessage = inputValue.trim();
    setInputValue("");
    setMessages(prev => [...prev, { type: "user", content: userMessage }]);
    setShowQuickOptions(false);
    setIsTyping(true);

    setTimeout(() => {
      setIsTyping(false);
      const category = analyzeInput(userMessage);
      
      if (category) {
        const suggestion = PRODUCT_SUGGESTIONS[category];
        setMessages(prev => [...prev, {
          type: "bot",
          content: `I found some great options for you! Here's what I recommend:`,
          products: suggestion.products
        }]);
      } else {
        setMessages(prev => [...prev, {
          type: "bot",
          content: "I'd love to help! Could you tell me more about what you're celebrating? Here are some popular options:",
        }]);
        setShowQuickOptions(true);
      }
    }, 1000);
  };

  const handleProductClick = (path) => {
    onClose();
    navigate(path);
  };

  if (!isOpen) return null;

  const modalContent = (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal Container */}
      <div 
        className="fixed z-[101] w-full max-w-lg px-4"
        style={{
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)'
        }}
        data-testid="product-finder-chat"
      >
        <div className="bg-[#1E293B] rounded-2xl shadow-2xl border border-[#D4AF37]/30 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/10 px-6 py-4 flex items-center justify-between border-b border-[#D4AF37]/20">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#D4AF37] flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#0F172A]" />
              </div>
              <div>
                <h2 className="font-cinzel text-[#D4AF37] font-bold">Happy AI Agent</h2>
                <p className="text-xs text-[#94A3B8]">Here to help you celebrate</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-[#94A3B8] hover:text-[#F8FAFC]"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Messages */}
          <div className="h-80 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.type === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] ${msg.type === "user" 
                  ? "bg-[#D4AF37] text-[#0F172A] rounded-2xl rounded-br-md" 
                  : "bg-[#0F172A] text-[#F8FAFC] rounded-2xl rounded-bl-md border border-[#D4AF37]/20"
                } px-4 py-3`}>
                  <p className="text-sm">{msg.content}</p>
                  
                  {/* Product Suggestions */}
                  {msg.products && (
                    <div className="mt-3 space-y-2">
                      {msg.products.map((product, pIdx) => (
                        <button
                          key={pIdx}
                          onClick={() => handleProductClick(product.path)}
                          className="w-full text-left bg-[#1E293B] hover:bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-3 transition-all group"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-[#D4AF37]">{product.icon}</span>
                            <span className="font-semibold text-[#F8FAFC] group-hover:text-[#D4AF37] transition-colors">
                              {product.name}
                            </span>
                          </div>
                          <p className="text-xs text-[#94A3B8] mt-1 ml-6">{product.desc}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {/* Typing Indicator */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-[#0F172A] rounded-2xl rounded-bl-md px-4 py-3 border border-[#D4AF37]/20">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-[#D4AF37] rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></span>
                    <span className="w-2 h-2 bg-[#D4AF37] rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></span>
                    <span className="w-2 h-2 bg-[#D4AF37] rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Quick Options */}
            {showQuickOptions && !isTyping && (
              <div className="flex flex-wrap gap-2 pt-2">
                {QUICK_OPTIONS.map((option) => (
                  <button
                    key={option.key}
                    onClick={() => handleQuickOption(option.key)}
                    className="px-3 py-2 text-sm bg-[#0F172A] hover:bg-[#D4AF37]/20 border border-[#D4AF37]/30 rounded-full text-[#F8FAFC] hover:text-[#D4AF37] transition-all"
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-[#D4AF37]/20 bg-[#0F172A]/50">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
                placeholder="Tell me what you're celebrating..."
                className="flex-1 bg-[#0F172A] border border-[#D4AF37]/30 rounded-full px-4 py-2 text-[#F8FAFC] placeholder:text-[#64748B] focus:outline-none focus:border-[#D4AF37] transition-colors"
                data-testid="chat-input"
              />
              <Button
                onClick={handleSend}
                disabled={!inputValue.trim()}
                className="bg-[#D4AF37] hover:bg-[#B5952F] text-[#0F172A] rounded-full px-4"
                data-testid="chat-send-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  // Use portal to render outside the header
  return createPortal(modalContent, document.body);
}
